import { IconCloseButton } from '@/components/Button/IconCloseButton'
import { Caption1, Heading4, Title1 } from '@/components/ui/typography'
import { Widget, WidgetType } from 'routes/Dashboard/types'

interface WidgetPaletteProps {
  onAddWidget: (widget: Widget) => void
  onRemoveWidget: (type: WidgetType) => void
  isOpen: boolean
  onClose: () => void
  existingWidgets: Widget[]
  availableWidgets: Widget[]
}

export const WidgetPalette: React.FC<WidgetPaletteProps> = ({
  onAddWidget,
  onRemoveWidget,
  isOpen,
  onClose,
  existingWidgets,
  availableWidgets,
}) => {
  const isWidgetAlreadyAdded = (widgetType: WidgetType) =>
    existingWidgets.some((widget) => widget.type === widgetType)

  const handleWidgetClick = (widget: Widget) => {
    if (isWidgetAlreadyAdded(widget.type)) {
      onRemoveWidget(widget.type)
    } else {
      onAddWidget(widget)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className='fixed inset-0 z-[1000] flex items-center justify-center bg-black/50'
      onClick={onClose}
    >
      <div
        className='flex max-h-[80vh] w-[90%] max-w-[600px] flex-col rounded-lg bg-gray-100 shadow-2xl'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='flex items-center justify-between border-b border-black/10 px-6 py-5'>
          <Heading4>{existingWidgets.length === 0 ? 'Add Widget' : 'Edit Widgets'}</Heading4>

          <IconCloseButton onClick={onClose} />
        </div>

        <div className='flex-1 overflow-y-auto p-6'>
          <div className='grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-4'>
            {availableWidgets.map((widget) => {
              const isDisabled = isWidgetAlreadyAdded(widget.type)

              return (
                <div
                  key={widget.id}
                  onClick={() => handleWidgetClick(widget)}
                  className={`
                    flex cursor-pointer items-center gap-3 rounded-lg border-2 p-4 transition-all duration-200
                    ${
                      isDisabled
                        ? 'cursor-not-allowed border-black/10 bg-gray-50 opacity-50'
                        : 'border-gray-400/40 hover:-translate-y-0.5 hover:border-pink-500 hover:bg-pink-200 hover:shadow-md'
                    }
                  `}
                >
                  <div className='flex-shrink-0 text-xl'>{getWidgetIcon(widget.type)}</div>

                  <div className='flex flex-1 flex-col gap-1'>
                    <Title1>{widget.title}</Title1>

                    {isDisabled && (
                      <Caption1 className='leading-none font-semibold italic text-red-500'>
                        Click to remove
                      </Caption1>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

function getWidgetIcon(type: WidgetType): string {
  // dummy icons
  switch (type) {
    case WidgetType.CJM:
    case WidgetType.NV_TRADED:
      return '📈'
    case WidgetType.DEPOSITS:
      return '💰'
    case WidgetType.ACTIVE_TA:
      return '💵'
    case WidgetType.FIRST_TIME_DEPOSIT:
      return '⚖️'
    case WidgetType.ACTIVE_CLIENTS:
      return '📞'
    case WidgetType.LOTS_TRADED:
      return '⚖️'
    case WidgetType.IB_COMMISSIONS:
      return '💰'
    case WidgetType.NEW_CLIENTS:
      return '📊'
    case WidgetType.IB_METRICS_COMPARISON:
    case WidgetType.NET_DEPOSITS:
      return '🌍'
    case WidgetType.OPENED_TA:
      return '📋'
    default:
      return '📊'
  }
}
